import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { useToken, useBreakpoint, DEFAULT_BREAKPOINT } from '@hub/design-system-base';
import { Reel } from '@hub/reel';
import { Frame } from '@hub/frame';
import { Stack } from '@hub/stack';
import { CoreContainerContext } from '@hub/core-container';
import Color from 'color';
import { CloudinaryImage, IMAGE_SIZES_FULL_WIDTH, IMAGE_SIZES_HERO } from '../cloudinary-image';
import { AsMeasured } from '../as-measured';
import { useWithIsAboveFoldProvider } from '../is-above-fold-provider';
import { isMemberExclusive } from '../../helpers/is-member-exclusive';
import { TrackLinkBox } from '../../analytics-observer/track-link-box';
import { FeatureControls } from './featureControls';
import { ContentStack } from './contentStack';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MAX_SLIDES = 5;
export function pickBestContrast(base, options) {
  let highestContrast = 0;
  let winningOptionIndex = 0;
  const baseColor = Color(base);
  for (let i = 0; i < options.length; i++) {
    const optionConstrast = Color(options[i]).contrast(baseColor);
    if (optionConstrast > highestContrast) {
      highestContrast = optionConstrast;
      winningOptionIndex = i;
    }
  }
  return options[winningOptionIndex];
}
function findSquareImage(image, secondaryImage) {
  if (image.type === 'SQUARE') {
    return image;
  }
  return secondaryImage || image;
}
function isLightColorText(color, threshold) {
  const [r, g, b] = Color(color).rgb().array();
  return (r + g + b) / 3 > threshold;
}
const findTallestSlide = slides => slides.filter(Boolean).reduce((tallestSlideHeight, slide) => {
  var _stackElement$firstEl, _stackElement$lastEle, _stackElement$lastEle2;
  const stackElement = slide === null || slide === void 0 ? void 0 : slide.lastElementChild;
  const image = (stackElement === null || stackElement === void 0 || (_stackElement$firstEl = stackElement.firstElementChild) === null || _stackElement$firstEl === void 0 ? void 0 : _stackElement$firstEl.scrollHeight) || 0;
  const title = (stackElement === null || stackElement === void 0 || (_stackElement$lastEle = stackElement.lastElementChild) === null || _stackElement$lastEle === void 0 || (_stackElement$lastEle = _stackElement$lastEle.firstElementChild) === null || _stackElement$lastEle === void 0 ? void 0 : _stackElement$lastEle.scrollHeight) || 0;
  const button = (stackElement === null || stackElement === void 0 || (_stackElement$lastEle2 = stackElement.lastElementChild) === null || _stackElement$lastEle2 === void 0 || (_stackElement$lastEle2 = _stackElement$lastEle2.lastElementChild) === null || _stackElement$lastEle2 === void 0 ? void 0 : _stackElement$lastEle2.scrollHeight) || 0;
  const stackHeight = image + title + button;
  return tallestSlideHeight < stackHeight ? stackHeight : tallestSlideHeight;
}, 0);
const Slide = _ref => {
  let {
    item,
    viewEvent,
    index,
    analyticsSlot,
    setHeight,
    slidesRef,
    clickEvent,
    containerGutter,
    sx
  } = _ref;
  const wideImageContentLeftOffset = '65%';
  const wideImageContentRightPadding = useToken('space', 'spacing-xl');
  const squareImagePadding = useToken('space', 'spacing-md');
  const defaultWideForegroundColor = useToken('colors', 'white');
  const defaultSquareForegroundColor = useToken('colors', 'licorice');
  const alternateSquareForegroundColor = useToken('colors', 'white');
  const defaultSupportingColor = useToken('colors', 'smoke');
  const {
    title,
    image,
    secondaryImage,
    shortDescription,
    callToAction
  } = item;
  const hasWideImage = image.type === 'WIDE';
  const squareImage = findSquareImage(image, secondaryImage);
  const squareSupportingColour = squareImage.supportingColour || defaultSupportingColor;
  const squareForegroundColor = pickBestContrast(squareSupportingColour, [defaultSquareForegroundColor, alternateSquareForegroundColor]);
  const contentActionLabel = callToAction.label;
  const contentTitle = title;
  const viewEventForItem = React.useCallback(item => {
    if (!viewEvent) {
      return undefined;
    }
    return () => viewEvent(item);
  }, [viewEvent]);
  const hasTextShadow = isLightColorText(item.textColour || defaultWideForegroundColor, 128);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [hasWideImage &&
    /*#__PURE__*/
    // We have a wide image so display in Hero Feature mode in desktop and square in mobile
    _jsx(Frame, {
      ratio: ['1', '1', '21/9'],
      sx: _objectSpread({
        display: ['none', 'none', 'block'],
        height: [null, null, 'size-full']
      }, sx),
      ref: setHeight,
      "data-id": "feature_index_Slide_Frame",
      children: /*#__PURE__*/_jsx(TrackLinkBox, {
        ref: el => {
          slidesRef.current[index] = el;
        },
        onCreateImpressionWhenView: viewEventForItem(item),
        "data-id": "feature_index_Slide_Frame_TrackLinkBox",
        children: /*#__PURE__*/_jsxs(Stack, {
          gap: ['spacing-md', 'spacing-md', 'spacing-none'],
          direction: ['column', 'column', 'row'],
          sx: {
            height: 'size-full',
            isolation: 'isolate',
            position: 'relative',
            width: 'size-full',
            justifyContent: [null, null, 'flex-end'],
            alignItems: [null, null, 'center'],
            paddingX: [containerGutter, containerGutter, 0],
            paddingY: [squareImagePadding, squareImagePadding, 0]
          },
          shouldWrapChildren: false,
          flexGrow: 0,
          "data-id": "feature_index_Slide_TrackLinkBox_Stack",
          children: [/*#__PURE__*/_jsx(CloudinaryImage, {
            ratio: '21/9',
            imageSetOrImage: image,
            sx: {
              zIndex: 1,
              position: 'absolute',
              width: '100%',
              height: '100%'
            },
            sizes: IMAGE_SIZES_FULL_WIDTH,
            "data-id": "feature_index_Slide_Stack_CloudinaryImage"
          }), /*#__PURE__*/_jsx(ContentStack, _objectSpread(_objectSpread({
            as: props => {
              var _item$orchestrationMe, _item$orchestrationMe2;
              return /*#__PURE__*/_jsx(AsMeasured, _objectSpread(_objectSpread({}, props), {}, {
                metadata: item.analyticsMetadata,
                slot: (_item$orchestrationMe = (_item$orchestrationMe2 = item.orchestrationMetadata) === null || _item$orchestrationMe2 === void 0 ? void 0 : _item$orchestrationMe2.displayId) !== null && _item$orchestrationMe !== void 0 ? _item$orchestrationMe : analyticsSlot,
                "data-id": "feature_index_Slide_AsMeasured"
              }));
            },
            position: index + 1,
            sx: {
              zIndex: 2,
              justifyContent: 'flex-end',
              maxWidth: "calc(100% - (".concat(wideImageContentLeftOffset, " + ").concat(wideImageContentRightPadding, "))"),
              marginRight: "".concat(wideImageContentRightPadding, " !important")
            },
            title: contentTitle,
            shortDescription: shortDescription !== null && shortDescription !== void 0 ? shortDescription : undefined,
            callToActionLabel: contentActionLabel,
            url: callToAction.url,
            color: item.textColour || defaultWideForegroundColor,
            hasHoverEffect: false
          }, hasTextShadow ? {
            textShadow: '0 0 12px rgba(0, 0, 0, 0.7)'
          } : undefined), {}, {
            memberExclusive: isMemberExclusive(item.highlightAttribute),
            onClick: () => {
              if (clickEvent) {
                clickEvent(index);
              }
            },
            "data-id": "feature_index_Slide_Stack_ContentStack"
          }))]
        })
      })
    }, "".concat(index, "-wide-frame")), /*#__PURE__*/_jsx(TrackLinkBox, {
      ref: el => {
        slidesRef.current[index] = el;
      },
      sx: _objectSpread(_objectSpread({}, sx), {}, {
        display: ['block', 'block', hasWideImage ? 'none' : 'block']
      }),
      onCreateImpressionWhenView: viewEventForItem(item),
      "data-id": "feature_index_Slide_TrackLinkBox",
      children: /*#__PURE__*/_jsxs(Stack, {
        gap: ['spacing-md', 'spacing-md', 'spacing-7'],
        direction: ['column', 'column', 'row'],
        shouldWrapChildren: false,
        sx: {
          alignItems: [null, null, 'center'],
          backgroundColor: squareSupportingColour,
          paddingY: squareImagePadding,
          paddingX: [containerGutter, containerGutter, 0],
          height: [null, 'size-full', null]
        },
        flexGrow: 0,
        "data-id": "feature_index_Slide_TrackLinkBox_Stack",
        children: [/*#__PURE__*/_jsx(CloudinaryImage, {
          ratio: "1",
          sx: {
            minWidth: [null, null, "calc((100% / (21 / 9)) - (2 * ".concat(squareImagePadding, "))")],
            marginLeft: [null, null, squareImagePadding]
          },
          imageSetOrImage: squareImage,
          sizes: IMAGE_SIZES_HERO,
          "data-id": "feature_index_Slide_Stack_CloudinaryImage"
        }), /*#__PURE__*/_jsx(ContentStack, {
          as: props => {
            var _item$orchestrationMe3, _item$orchestrationMe4;
            return /*#__PURE__*/_jsx(AsMeasured, _objectSpread(_objectSpread({}, props), {}, {
              metadata: item.analyticsMetadata,
              slot: (_item$orchestrationMe3 = (_item$orchestrationMe4 = item.orchestrationMetadata) === null || _item$orchestrationMe4 === void 0 ? void 0 : _item$orchestrationMe4.displayId) !== null && _item$orchestrationMe3 !== void 0 ? _item$orchestrationMe3 : analyticsSlot,
              "data-id": "feature_index_Slide_AsMeasured"
            }));
          },
          position: index + 1,
          title: contentTitle,
          shortDescription: shortDescription !== null && shortDescription !== void 0 ? shortDescription : undefined,
          callToActionLabel: contentActionLabel,
          url: callToAction.url,
          color: squareForegroundColor,
          sx: {
            paddingRight: [null, null, 'spacing-xl'],
            flexGrow: [1, 1, null],
            justifyContent: [null, 'space-between', null]
          },
          memberExclusive: isMemberExclusive(item.highlightAttribute),
          onClick: () => {
            if (clickEvent) {
              clickEvent(index);
            }
          },
          "data-id": "feature_index_Slide_Stack_ContentStack"
        })]
      })
    }, index)]
  });
};

// The logic in this component can be hard to grok at first read so here is a
// pseudocode summary of the behaviour:
//
// if wide image present
//   display hero feature mode for desktop
//   display square image for mobile (fall back to only available image even if its wide)
//  else
//    display in square desktop mode with square image

export const Feature = _ref2 => {
  let {
    slides,
    analyticsSlot,
    clickEvent,
    viewEvent
  } = _ref2;
  const breakpoint = useBreakpoint(DEFAULT_BREAKPOINT);
  const container = useContext(CoreContainerContext);
  const containerGutter = useToken('space', container.gutter);
  const slidesRef = React.useRef([]);
  const {
    0: _windowWidth,
    1: setWindowWidth
  } = useState();
  const {
    0: _frameHeight,
    1: setFrameHeight
  } = useState();
  useEffect(() => {
    const DELAY = 150;
    const updateSize = debounce(function () {
      if (window) {
        setFrameHeight(findTallestSlide(slidesRef.current));
      }
    }, DELAY);
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);
  const renderControls = useMemo(() => FeatureControls(breakpoint), [breakpoint]);
  const {
    0: oldFrameHeight,
    1: setOldFrameHeight
  } = useState(0);
  // determine the height of the element inside the frame (for mobile use)
  const setHeight = useCallback(element => {
    var _element$lastElementC, _stackElement$firstEl2, _stackElement$lastEle3, _stackElement$lastEle4;
    if (!element) {
      return;
    }
    const stackElement = (_element$lastElementC = element.lastElementChild) === null || _element$lastElementC === void 0 ? void 0 : _element$lastElementC.lastElementChild;
    const imageHeight = (stackElement === null || stackElement === void 0 || (_stackElement$firstEl2 = stackElement.firstElementChild) === null || _stackElement$firstEl2 === void 0 ? void 0 : _stackElement$firstEl2.scrollHeight) || 0;
    const titleHeight = (stackElement === null || stackElement === void 0 || (_stackElement$lastEle3 = stackElement.lastElementChild) === null || _stackElement$lastEle3 === void 0 || (_stackElement$lastEle3 = _stackElement$lastEle3.firstElementChild) === null || _stackElement$lastEle3 === void 0 ? void 0 : _stackElement$lastEle3.scrollHeight) || 0;
    const buttonHeight = (stackElement === null || stackElement === void 0 || (_stackElement$lastEle4 = stackElement.lastElementChild) === null || _stackElement$lastEle4 === void 0 || (_stackElement$lastEle4 = _stackElement$lastEle4.lastElementChild) === null || _stackElement$lastEle4 === void 0 ? void 0 : _stackElement$lastEle4.scrollHeight) || 0;
    const stackHeight = imageHeight + titleHeight + buttonHeight;
    // determine the maximum height of stacks and that will be used on the height of the frame
    setFrameHeight(stackHeight > oldFrameHeight ? stackHeight : oldFrameHeight);
    setOldFrameHeight(stackHeight);
  }, [oldFrameHeight]);
  useEffect(() => {
    function updateSize() {
      if (window) {
        setWindowWidth(window.innerWidth);
      }
    }
    setWindowWidth(0);
    setFrameHeight(undefined);
    setOldFrameHeight(0);
    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);
  const slideWithFold = useWithIsAboveFoldProvider(Slide);
  return /*#__PURE__*/_jsx(Reel, {
    snap: true,
    includeOverflowInWidthCalculation: true,
    visibleFrames: 1,
    gap: "spacing-none",
    renderControls: renderControls,
    containerOverflow: [containerGutter, null, 'spacing-none'],
    "data-id": "feature_index_Slide_Reel",
    children: slides.slice(0, MAX_SLIDES).map((item, index) => {
      const SlideWithFold = slideWithFold(index < 1 ? 'above' : 'below');
      return /*#__PURE__*/_jsx(SlideWithFold, {
        slidesRef,
        viewEvent,
        item,
        index,
        setHeight,
        analyticsSlot,
        clickEvent,
        containerGutter,
        "data-id": "feature_index_Slide_SlideWithFold"
      }, index);
    })
  });
};